import { useMemo, useState, useEffect, useRef } from "react";
import { useProduct } from "@/features/product/product-context";
import { MobileMarketSheet } from "@/header/header-product-select";
import { useProductSelection } from "@/header/use-product-selection";
import { ChevronDownIcon } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import { MARKETS } from "@/solana/constants";
import { useMarket, useMarkets } from "@/solana/hooks/use-markets";
import { ProductIcon } from "@/components/product-icon";
import { useMarketStat } from "@/backend/hooks/use-market-stats";
import { useMarketPrice, useOraclePrice } from "@/backend/store";

export const MobileMarketHeader = () => {
  const { product } = useProduct();
  const { selectedProduct } = useProductSelection();
  const [marketSheetOpen, setMarketSheetOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if current product is a perpetual market
  const isPerpMarket = useMemo(() => {
    return product?.id && product.id in MARKETS;
  }, [product?.id]);

  // Fetch market data for perpetuals (for metadata like openInterest, fundingRate)
  const { market } = useMarket(isPerpMarket ? product?.id : "BTC-USD");
  
  // Get markets data for 24H change
  const { markets } = useMarkets();
  
  // Get real-time prices from Zustand store (WebSocket updates)
  const liveMarketPrice = useMarketPrice(product?.id || "BTC-USD");
  const liveOraclePrice = useOraclePrice(product?.id || "BTC-USD");
  
  // Get real market stats from backend (includes open interest and funding)
  const marketStat = useMarketStat(product?.id || "BTC-USD");

  const prevPrice = useRef(0);
  const prevSentiment = useRef<string | undefined>(undefined);

  useEffect(() => {
    const value = liveMarketPrice || 0;
    if (value === 0 || value === prevPrice.current) return;

    prevSentiment.current =
      value > prevPrice.current
        ? "text-bid-foreground"
        : "text-ask-foreground";
    prevPrice.current = value;
  }, [liveMarketPrice]);

  const stats = useMemo(() => {
    if (!product) return null;

    const currentPrice = liveMarketPrice || 0;
    
    const priceClassName =
      currentPrice === prevPrice.current
        ? prevSentiment.current
        : currentPrice > prevPrice.current
        ? "text-bid-foreground"
        : "text-ask-foreground";

    // Get 24h change from markets data
    const marketData = markets?.find(m => m.symbol === product.id);
    const priceChange24h = marketData?.priceChange24h;
    const priceChangePercent24h = marketData?.priceChangePercent24h;
    
    // Format price change display
    const priceChangeDisplay = priceChange24h !== undefined
      ? (priceChange24h >= 0 ? "+" : "") + formatCurrency(Math.abs(priceChange24h), 2)
      : "-";
    const changePercentDisplay = priceChangePercent24h !== undefined
      ? (priceChangePercent24h >= 0 ? "+" : "") + priceChangePercent24h.toFixed(2) + "%"
      : "-";
    const changeClassName = priceChangePercent24h === undefined || priceChangePercent24h === 0
      ? "text-muted-foreground"
      : priceChangePercent24h > 0 ? "text-bid-foreground" : "text-ask-foreground";

    const baseStats = {
      currentPrice: formatCurrency(currentPrice, product.quote.precision),
      priceClassName,
      priceChange: priceChangeDisplay,
      changePercent: changePercentDisplay,
      changeClassName,
      volume: "-",
      markPrice: undefined as string | undefined,
      oraclePrice: undefined as string | undefined,
      openInterest: undefined as string | undefined,
      funding: undefined as string | undefined,
      fundingClassName: undefined as string | undefined,
    };

    // Extended stats for perpetual markets
    if (isPerpMarket && market) {
      const openInterestUsd = marketStat?.openInterestUsd ?? (market.openInterest * currentPrice);
      const volume24hUsd = marketStat?.volume24hUsd ?? 0;
      const fundingRate = marketStat?.fundingRate ?? market.fundingRate;
      const nextFundingTime = marketStat?.nextFundingTime ?? market.nextFundingTime;

      // Use real-time prices from Zustand store
      baseStats.markPrice = formatCurrency(currentPrice, product.quote.precision);
      baseStats.oraclePrice = formatCurrency(liveOraclePrice || market.oraclePrice, product.quote.precision);
      baseStats.volume = formatOpenInterestUsd(volume24hUsd);
      baseStats.openInterest = formatOpenInterestUsd(openInterestUsd);
      baseStats.funding = formatFundingCountdown(fundingRate, nextFundingTime);
      baseStats.fundingClassName = fundingRate >= 0 ? "text-bid-foreground" : "text-ask-foreground";
    }

    return baseStats;
  }, [product, isPerpMarket, market, marketStat, liveMarketPrice, liveOraclePrice, markets]);

  if (!stats) return null;

  return (
    <>
      <div className="border-b bg-background">
        {/* 50/50 Split Header Row */}
        <div className="flex">
          {/* Left 50%: Market Selector */}
          <button
            type="button"
            onClick={() => setMarketSheetOpen(true)}
            className="flex-1 flex items-center justify-between px-3 py-2 hover:bg-muted/30 active:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              {selectedProduct && (
                <ProductIcon baseCode={selectedProduct.base.code} className="size-6" />
              )}
              <span className="text-sm font-semibold">
                {selectedProduct?.symbol || "Select Market"}
              </span>
            </div>
            <ChevronDownIcon className="size-4 text-muted-foreground" />
          </button>

          {/* Divider */}
          <div className="w-px bg-border" />

          {/* Right 50%: Price and Stats */}
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-1 flex items-center justify-between px-3 py-2 hover:bg-muted/30 active:bg-muted/50 transition-colors"
          >
            <div className="flex flex-col items-start">
              <span className={cn("text-sm font-medium tabular-nums", stats.priceClassName)}>
                {stats.currentPrice}
              </span>
              <span className={cn("text-xs tabular-nums", stats.changeClassName)}>
                {stats.priceChange} / {stats.changePercent}
              </span>
            </div>
            <ChevronDownIcon
              className={cn(
                "size-4 text-muted-foreground transition-transform duration-200",
                isExpanded && "rotate-180"
              )}
            />
          </button>
        </div>

        {/* Expandable Stats Section */}
        <div
          className={cn(
            "overflow-hidden transition-all duration-200",
            isExpanded ? "max-h-48" : "max-h-0"
          )}
        >
          <div className="px-3 pb-3 pt-1 grid grid-cols-2 gap-x-4 gap-y-2 text-xs border-t">
            {isPerpMarket && stats.markPrice ? (
              <>
                <div>
                  <div className="text-muted-foreground">Mark Price</div>
                  <div className="font-medium text-foreground">{stats.markPrice}</div>
                </div>
                
                <div className="text-right">
                  <div className="text-muted-foreground">Oracle Price</div>
                  <div className="font-medium text-foreground">{stats.oraclePrice}</div>
                </div>

                <div>
                  <div className="text-muted-foreground">24h Volume</div>
                  <div className="font-medium text-foreground">{stats.volume}</div>
                </div>
                
                <div className="text-right">
                  <div className="text-muted-foreground">Open Interest</div>
                  <div className="font-medium text-foreground">{stats.openInterest}</div>
                </div>

                <div className="col-span-2">
                  <div className="text-muted-foreground">Funding / Countdown</div>
                  <div className={cn("font-medium", stats.fundingClassName)}>
                    {stats.funding}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <div className="text-muted-foreground">Last Price</div>
                  <div className={cn("font-medium", stats.priceClassName)}>
                    {stats.currentPrice}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-muted-foreground">24h Volume</div>
                  <div className="font-medium text-foreground">{stats.volume}</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Market Selection Sheet */}
      <MobileMarketSheet 
        open={marketSheetOpen} 
        onOpenChange={setMarketSheetOpen} 
      />
    </>
  );
};

// Ticker data now comes from backend WebSocket

const formatOpenInterestUsd = (value: number): string => {
  if (value === 0) return "$0";
  
  return "$" + new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatFundingCountdown = (
  fundingRate: number,
  nextFundingTime: number
): string => {
  const fundingRatePercent = (fundingRate * 100).toFixed(4);
  const sign = fundingRate >= 0 ? "+" : "";

  const now = Date.now();
  const diff = nextFundingTime - now;

  if (diff <= 0) return `${sign}${fundingRatePercent}% / Soon`;

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return `${sign}${fundingRatePercent}% / ${hours}h ${minutes}m`;
};

